/**
 * ImageUploadField - Shared image upload component for booking/service request forms
 * 
 * Features:
 * - Mobile: Camera capture or photo library selection
 * - Desktop: File browser
 * - Client-side compression (max 1600px, 0.75 quality)
 * - EXIF orientation fix
 * - Max 8 images
 * - Lightbox preview
 */
import React, { useState, useRef, useCallback } from 'react';
import { useLanguage } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { 
  ImagePlus, X, Loader2, Camera, Image as ImageIcon,
  ChevronLeft, ChevronRight, ZoomIn
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// i18n labels
const LABELS = {
  et: {
    addImages: 'Lisa pildid',
    optional: 'valikuline',
    uploadImage: 'Lisa pilt',
    takePhoto: 'Tee foto',
    fromGallery: 'Galeriist',
    uploading: 'Laadin...',
    imageUploaded: 'Pilt üles laaditud',
    maxImages: 'Maksimaalselt 8 pilti',
    invalidImageType: 'Lubatud ainult JPEG, PNG, WebP',
    imageTooLarge: 'Pilt liiga suur (max 10MB)',
    uploadFailed: 'Pildi üleslaadimine ebaõnnestus',
    hint: 'JPEG, PNG, WebP. Max 10MB. Kuni 8 pilti.',
    clickToEnlarge: 'Klõpsa suurendamiseks'
  },
  en: {
    addImages: 'Add Images',
    optional: 'optional',
    uploadImage: 'Add Image',
    takePhoto: 'Take Photo',
    fromGallery: 'From Gallery',
    uploading: 'Uploading...',
    imageUploaded: 'Image uploaded',
    maxImages: 'Maximum 8 images',
    invalidImageType: 'Only JPEG, PNG, WebP allowed',
    imageTooLarge: 'Image too large (max 10MB)',
    uploadFailed: 'Failed to upload image',
    hint: 'JPEG, PNG, WebP. Max 10MB. Up to 8 images.',
    clickToEnlarge: 'Click to enlarge'
  },
  fi: {
    addImages: 'Lisää kuvia',
    optional: 'valinnainen',
    uploadImage: 'Lisää kuva',
    takePhoto: 'Ota kuva',
    fromGallery: 'Galleriasta',
    uploading: 'Ladataan...',
    imageUploaded: 'Kuva ladattu',
    maxImages: 'Enintään 8 kuvaa',
    invalidImageType: 'Vain JPEG, PNG, WebP sallittu',
    imageTooLarge: 'Kuva liian suuri (max 10MB)',
    uploadFailed: 'Kuvan lataus epäonnistui',
    hint: 'JPEG, PNG, WebP. Max 10MB. Enintään 8 kuvaa.',
    clickToEnlarge: 'Klikkaa suurentaaksesi'
  },
  ru: {
    addImages: 'Добавить фото',
    optional: 'необязательно',
    uploadImage: 'Добавить фото',
    takePhoto: 'Сделать фото',
    fromGallery: 'Из галереи',
    uploading: 'Загрузка...',
    imageUploaded: 'Фото загружено',
    maxImages: 'Максимум 8 фото',
    invalidImageType: 'Разрешены только JPEG, PNG, WebP',
    imageTooLarge: 'Фото слишком большое (макс. 10МБ)',
    uploadFailed: 'Не удалось загрузить фото',
    hint: 'JPEG, PNG, WebP. Макс. 10МБ. До 8 фото.',
    clickToEnlarge: 'Нажмите для увеличения'
  }
};

/**
 * Compress and resize image before upload
 * - Max dimension: 1600px
 * - Quality: 0.75
 * - Handles EXIF orientation
 */
const compressImage = (file, maxDimension = 1600, quality = 0.75) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;
        
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        // White background for transparency
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        
        // Draw image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create new file with same name
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Lightbox component for viewing images
 */
const ImageLightbox = ({ images, currentIndex, isOpen, onClose, onNext, onPrev }) => {
  if (!isOpen || images.length === 0) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 bg-black/95 border-none">
        <div className="relative flex items-center justify-center min-h-[60vh]">
          {/* Close button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
          >
            <X className="h-6 w-6" />
          </button>
          
          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={onPrev}
                className="absolute left-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={onNext}
                className="absolute right-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
          
          {/* Image */}
          <img 
            src={images[currentIndex]} 
            alt={`Image ${currentIndex + 1}`}
            className="max-h-[80vh] max-w-full object-contain"
          />
          
          {/* Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
            {currentIndex + 1} / {images.length}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Main ImageUploadField component
 */
export const ImageUploadField = ({ 
  images = [], 
  onImagesChange, 
  maxImages = 8,
  uploadEndpoint = '/api/workshop-requests/upload-image',
  className = ''
}) => {
  const { language } = useLanguage();
  const labels = LABELS[language] || LABELS.en;
  
  const [uploading, setUploading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  const handleFileSelect = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Check max images
    if (images.length + files.length > maxImages) {
      toast.error(labels.maxImages);
      return;
    }
    
    setUploading(true);
    
    for (const file of files) {
      try {
        // Validate type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
          toast.error(labels.invalidImageType);
          continue;
        }
        
        // Validate size (10MB)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(labels.imageTooLarge);
          continue;
        }
        
        // Compress image
        let fileToUpload = file;
        try {
          fileToUpload = await compressImage(file);
        } catch (compressError) {
          console.warn('Compression failed, using original:', compressError);
        }
        
        // Upload
        const formData = new FormData();
        formData.append('file', fileToUpload);
        
        const res = await fetch(`${API_URL}${uploadEndpoint}`, {
          method: 'POST',
          body: formData
        });
        
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || labels.uploadFailed);
        }
        
        const data = await res.json();
        
        if (data.url) {
          onImagesChange([...images, data.url]);
          toast.success(labels.imageUploaded);
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error.message || labels.uploadFailed);
      }
    }
    
    setUploading(false);
    
    // Reset inputs
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  }, [images, maxImages, uploadEndpoint, onImagesChange, labels]);
  
  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };
  
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };
  
  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % images.length);
  };
  
  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
  };
  
  // Detect if mobile
  const isMobile = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  return (
    <div className={cn("space-y-3", className)} data-testid="image-upload-field">
      <Label className="flex items-center gap-2">
        <ImageIcon className="h-4 w-4" />
        {labels.addImages} ({images.length}/{maxImages})
        <span className="text-xs text-muted-foreground font-normal">({labels.optional})</span>
      </Label>
      
      {/* Uploaded images preview */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((url, idx) => (
            <div 
              key={idx} 
              className="relative group"
              data-testid={`uploaded-image-${idx}`}
            >
              <img 
                src={url} 
                alt={`Upload ${idx + 1}`}
                className="h-20 w-20 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition"
                onClick={() => openLightbox(idx)}
              />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeImage(idx); }}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition shadow-md"
                data-testid={`remove-image-${idx}`}
              >
                <X className="h-3 w-3" />
              </button>
              <div 
                className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition rounded-lg pointer-events-none"
              >
                <ZoomIn className="h-5 w-5 text-white" />
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Upload buttons */}
      {images.length < maxImages && (
        <div className="flex flex-wrap gap-2">
          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
            multiple
            data-testid="file-input"
          />
          
          {/* Camera input for mobile */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="camera-input"
          />
          
          {uploading ? (
            <Button type="button" variant="outline" disabled className="gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {labels.uploading}
            </Button>
          ) : (
            <>
              {/* Mobile: Show separate camera and gallery buttons */}
              {isMobile ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => cameraInputRef.current?.click()}
                    className="gap-2 border-dashed"
                    data-testid="camera-button"
                  >
                    <Camera className="h-4 w-4" />
                    {labels.takePhoto}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="gap-2 border-dashed"
                    data-testid="gallery-button"
                  >
                    <ImagePlus className="h-4 w-4" />
                    {labels.fromGallery}
                  </Button>
                </>
              ) : (
                /* Desktop: Single upload button */
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2 border-dashed border-2 hover:border-orange-500 hover:bg-orange-50"
                  data-testid="upload-button"
                >
                  <ImagePlus className="h-4 w-4" />
                  {labels.uploadImage}
                </Button>
              )}
            </>
          )}
        </div>
      )}
      
      <p className="text-xs text-muted-foreground">{labels.hint}</p>
      
      {/* Lightbox */}
      <ImageLightbox
        images={images}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNext={nextImage}
        onPrev={prevImage}
      />
    </div>
  );
};

/**
 * ImageGallery - Display images with lightbox (for detail views)
 */
export const ImageGallery = ({ images = [], className = '' }) => {
  const { language } = useLanguage();
  const labels = LABELS[language] || LABELS.en;
  
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  if (!images || images.length === 0) return null;
  
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };
  
  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % images.length);
  };
  
  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
  };
  
  return (
    <div className={cn("space-y-2", className)} data-testid="image-gallery">
      <Label className="flex items-center gap-2 text-sm">
        <ImageIcon className="h-4 w-4" />
        {labels.addImages} ({images.length})
      </Label>
      
      <div className="flex flex-wrap gap-2">
        {images.map((url, idx) => (
          <div 
            key={idx} 
            className="relative group cursor-pointer"
            onClick={() => openLightbox(idx)}
            data-testid={`gallery-image-${idx}`}
          >
            <img 
              src={url} 
              alt={`Image ${idx + 1}`}
              className="h-24 w-24 object-cover rounded-lg border hover:opacity-90 transition shadow-sm"
            />
            <div 
              className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition rounded-lg"
            >
              <ZoomIn className="h-6 w-6 text-white" />
            </div>
          </div>
        ))}
      </div>
      
      <ImageLightbox
        images={images}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNext={nextImage}
        onPrev={prevImage}
      />
    </div>
  );
};

export default ImageUploadField;
